import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useCompany } from "@/hooks/useCompanies";
import { useCompanyConnection } from "@/hooks/useFreeAgent";
import { useCreateShareReport } from "@/hooks/useShareReport";
import ShareReportDialog from "@/components/ShareReportDialog";

export default function CompanyOverview() {
  const { companyId } = useParams<{ companyId: string }>();
  const id = companyId ? Number(companyId) : undefined;
  const company = useCompany(id);
  const connection = useCompanyConnection(id);

  const [shareOpen, setShareOpen] = useState(false);
  const share = useCreateShareReport();

  if (company.isLoading) return <div className="text-muted-foreground">Loading…</div>;
  if (!company.data) return <div>Company not found.</div>;

  const c = company.data;
  const canShare = !!connection.data && connection.data.last_synced_at != null;

  return (
    <div className="space-y-8">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link
            to="/dashboard/app/company"
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            ← My company
          </Link>
          <h1 className="mt-2 text-3xl font-semibold">{c.name}</h1>
          <p className="mt-1 text-muted-foreground">
            {c.country} · {c.company_type} · {c.currency}
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setShareOpen(true);
            share.reset();
            if (id) share.mutate(id);
          }}
          disabled={!id || share.isPending || !canShare}
          title={
            canShare
              ? "Generate a public read-only link to this dashboard"
              : "Connect FreeAgent and sync once before sharing"
          }
          className="rounded-md border px-3 py-2 text-sm font-medium hover:bg-secondary disabled:opacity-50"
        >
          {share.isPending ? "Generating…" : "Share dashboard"}
        </button>
      </header>

      <section className="rounded-lg border p-6">
        <h2 className="text-lg font-semibold">Company details</h2>
        <dl className="mt-4 grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
          {c.registration_number ? (
            <DetailRow label="Companies House number" value={c.registration_number} />
          ) : null}
          {c.sic_code ? <DetailRow label="SIC code" value={c.sic_code} /> : null}
          <DetailRow
            label="Financial year end"
            value={`${c.financial_year_end_day} ${monthName(c.financial_year_end_month)}`}
          />
          <DetailRow
            label="VAT"
            value={c.vat_registered ? c.vat_number || "Registered" : "Not registered"}
          />
          {c.next_accounts_due ? (
            <DetailRow label="Next accounts due" value={formatDate(c.next_accounts_due)} />
          ) : null}
          {c.next_confirmation_statement_due ? (
            <DetailRow
              label="Next confirmation statement"
              value={formatDate(c.next_confirmation_statement_due)}
            />
          ) : null}
        </dl>
      </section>

      <section className="rounded-lg border p-6">
        <h2 className="text-lg font-semibold">Accounting connection</h2>
        {connection.isLoading ? (
          <p className="mt-2 text-muted-foreground">Loading…</p>
        ) : connection.data ? (
          <div className="mt-3 space-y-2 text-sm">
            <div>
              <span className="text-muted-foreground">Provider:</span>{" "}
              <span className="font-medium">{connection.data.provider}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Status:</span>{" "}
              <span className="font-medium">{connection.data.status}</span>
            </div>
            {connection.data.last_synced_at ? (
              <div>
                <span className="text-muted-foreground">Last sync:</span>{" "}
                <span className="text-muted-foreground">
                  {new Date(connection.data.last_synced_at).toLocaleString()}
                </span>
              </div>
            ) : null}
            <Link
              to="/dashboard/app/integrations"
              className="mt-3 inline-block text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            >
              Manage in Integrations →
            </Link>
          </div>
        ) : (
          <div className="mt-3">
            <p className="text-sm text-muted-foreground">
              No accounting account connected.
            </p>
            <Link
              to="/dashboard/app/integrations"
              className="mt-3 inline-block rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
            >
              Set up an integration →
            </Link>
          </div>
        )}
      </section>

      <ShareReportDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        token={share.data?.token ?? null}
        expiresAt={share.data?.expires_at ?? null}
        isPending={share.isPending}
        errorMessage={
          share.isError
            ? ((share.error as Error)?.message ?? "Failed to generate share link.")
            : null
        }
      />
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="mt-0.5">{value}</dd>
    </div>
  );
}

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function monthName(m: number): string {
  if (m < 1 || m > 12) return "—";
  return MONTHS[m - 1];
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}
